import { bounds, getStyle } from "./studioModel";
import { resizeElement } from "./studioResize";

// Precise transform semantics for the inspector (plan §4). Four resize
// intentions are kept distinct:
//   frame   — change the frame only; text re-wraps, type size is unchanged
//   content — scale the frame and what is inside it (type size, tracking)
//   group   — scale the selection as one object around a shared origin
//   each    — scale every element by the same factor around its own origin
export const RESIZE_MODES = [
  ["frame", "Resize frame"],
  ["content", "Scale content"],
  ["group", "Scale as group"],
  ["each", "Resize each"],
];
export const ORIGINS = ["tl", "t", "tr", "l", "c", "r", "bl", "b", "br"];
const ORIGIN_FRACTIONS = { tl: [0, 0], t: [0.5, 0], tr: [1, 0], l: [0, 0.5], c: [0.5, 0.5], r: [1, 0.5], bl: [0, 1], b: [0.5, 1], br: [1, 1] };
const round = (n) => Math.round(n * 10000) / 10000;

// A shared value, or { mixed: true } when the selection disagrees.
export function mixedValue(values) {
  const finite = values.filter((v) => v !== undefined);
  if (!finite.length) return { value: undefined, mixed: false };
  const first = finite[0];
  return finite.every((v) => v === first) ? { value: first, mixed: false } : { value: undefined, mixed: true };
}
export function selectionValue(doc, nodes, key) {
  return mixedValue(nodes.map((n) => (["x", "y", "w", "h", "rotation", "opacity"].includes(key) ? n[key] : getStyle(doc, n)[key])));
}

// "12" absolute · "+2" / "+=2" / "-=2" relative · "*1.5" "/2" · "150%".
// A leading "-" alone is an absolute negative number.
export function parseEntry(input) {
  const text = String(input).trim().replace(/\s+/g, "").replace(/[−–]/g, "-");
  if (!text) return null;
  let m;
  if ((m = /^\+=?(-?\d*\.?\d+)$/.exec(text))) return { relative: true, apply: (v) => v + Number(m[1]) };
  if ((m = /^-=(\d*\.?\d+)$/.exec(text))) return { relative: true, apply: (v) => v - Number(m[1]) };
  if ((m = /^[*×x](\d*\.?\d+)$/.exec(text))) return { relative: true, apply: (v) => v * Number(m[1]) };
  if ((m = /^\/(\d*\.?\d+)$/.exec(text)) && Number(m[1]) !== 0) return { relative: true, apply: (v) => v / Number(m[1]) };
  if ((m = /^(\d*\.?\d+)%$/.exec(text))) return { relative: true, apply: (v) => v * Number(m[1]) / 100 };
  if (/^-?\d*\.?\d+$/.test(text)) return { relative: false, apply: () => Number(text), value: Number(text) };
  return null;
}

function anchorPoint(box, origin) {
  const [fx, fy] = ORIGIN_FRACTIONS[origin] || ORIGIN_FRACTIONS.tl;
  return { x: box.x + box.w * fx, y: box.y + box.h * fy };
}
function scaleType(doc, node, factor) {
  if (node.type !== "text") return {};
  const s = getStyle(doc, node);
  const style = { ...node.style, fontSize: round(s.fontSize * factor) };
  if (s.letterSpacing) style.letterSpacing = round(s.letterSpacing * factor);
  return { style, originalLayout: undefined };
}

// Returns patches { id → changed node } for a width/height entry.
export function resizeSelection(doc, nodes, { key, entry, mode = "frame", origin = "tl", lockAspect = false, headings = new Map() }) {
  const editable = nodes.filter((n) => !n.locked);
  if (!editable.length) return new Map();
  const group = bounds(editable);
  const out = new Map();
  if (mode === "group") {
    const current = group[key];
    const next = entry.apply(current);
    if (!(next > 0)) return out;
    const f = next / current;
    const fx = key === "w" || lockAspect ? f : 1, fy = key === "h" || lockAspect ? f : 1;
    const o = anchorPoint(group, origin);
    for (const n of editable) out.set(n.id, {
      ...n, x: round(o.x + (n.x - o.x) * fx), y: round(o.y + (n.y - o.y) * fy), w: round(n.w * fx), h: round(n.h * fy),
      ...(fx === fy ? scaleType(doc, n, fx) : {}),
    });
    return out;
  }
  // frame: each frame gets the entered size (or its own size ± the entry).
  // each/content: one factor, taken from the key (first) element, applied
  // proportionally to every element around its own origin.
  const keyNode = editable[0];
  const shared = entry.apply(keyNode[key]) / keyNode[key];
  for (const n of editable) {
    const f = mode === "frame" ? entry.apply(n[key]) / n[key] : shared;
    if (!(f > 0) || !Number.isFinite(f)) continue;
    const proportional = mode !== "frame" || lockAspect;
    const fx = key === "w" || proportional ? f : 1, fy = key === "h" || proportional ? f : 1;
    if (n.headingWinglet) { out.set(n.id, resizeElement(n, fx, fy, "se", headings.get(n.attachedTo))); continue; }
    const o = anchorPoint(n, origin);
    out.set(n.id, {
      ...n, x: round(o.x - (o.x - n.x) * fx), y: round(o.y - (o.y - n.y) * fy), w: round(n.w * fx), h: round(n.h * fy),
      ...(mode === "content" ? scaleType(doc, n, f) : {}),
    });
  }
  return out;
}

// X/Y/rotation/opacity entries: absolute values place the selection as a
// whole (its bounding box for x/y); relative values apply to each element.
export function moveSelection(nodes, { key, entry }) {
  const editable = nodes.filter((n) => !n.locked);
  const out = new Map();
  if (!editable.length) return out;
  if (["x", "y"].includes(key) && !entry.relative && editable.length > 1) {
    const box = bounds(editable), delta = entry.value - box[key];
    editable.forEach((n) => out.set(n.id, { ...n, [key]: round(n[key] + delta) }));
    return out;
  }
  editable.forEach((n) => {
    let value = entry.apply(key === "opacity" ? n.opacity * 100 : n[key]);
    if (key === "opacity") value = Math.max(0, Math.min(100, value)) / 100;
    out.set(n.id, { ...n, [key]: round(value) });
  });
  return out;
}

// Alignment targets: the selection's bounds, a key object (which stays put),
// the containing column (section block or group), or the page margins.
export const ALIGN_TARGETS = [["selection", "Selection"], ["key", "Key object"], ["column", "Column"], ["page", "Page"]];
export function alignSelection(page, nodes, direction, target, keyId) {
  const editable = nodes.filter((n) => !n.locked);
  if (!editable.length) return new Map();
  const margins = { x: page.margin, y: page.margin, w: page.width - page.margin * 2, h: page.height - page.margin * 2 };
  let box;
  if (target === "page" || (target === "selection" && editable.length === 1)) box = margins;
  else if (target === "key") box = (editable.find((n) => n.id === keyId) || editable[0]);
  else if (target === "column") {
    // The outermost layer group shared by the selection, else the page.
    const paths = editable.map((n) => (n.groupPath || []).map((g) => g.id));
    const shared = paths[0].find((id) => paths.every((p) => p.includes(id)));
    const members = shared ? page.elements.filter((n) => (n.groupPath || []).some((g) => g.id === shared)) : [];
    box = members.length ? bounds(members) : margins;
  } else box = bounds(editable);
  const out = new Map();
  for (const n of editable) {
    if (target === "key" && n.id === box.id) continue;
    const patch = {
      left: { x: box.x }, center: { x: box.x + (box.w - n.w) / 2 }, right: { x: box.x + box.w - n.w },
      top: { y: box.y }, middle: { y: box.y + (box.h - n.h) / 2 }, bottom: { y: box.y + box.h - n.h },
    }[direction];
    out.set(n.id, { ...n, ...Object.fromEntries(Object.entries(patch).map(([k, v]) => [k, round(v)])) });
  }
  return out;
}
